'use client';

import * as React from 'react';

import { Dialog } from '../modal';
import { Box } from '../box';
import { HStack } from '../stack';
import { Text } from '../text';
import { Button } from '../button';

export interface BaseModalProps {
  /**
   * Whether the modal is open
   */
  isOpen: boolean;
  /**
   * Callback when the modal should close
   */
  onClose: () => void;
  /**
   * Modal title (optional)
   */
  title?: React.ReactNode;
  /**
   * Modal size: 'sm' | 'md' | 'lg' | 'xl' | 'full'
   */
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  /**
   * Modal content
   */
  children: React.ReactNode;
  /**
   * Footer content (optional)
   */
  footer?: React.ReactNode;
  /**
   * Whether to show the close button in the header
   */
  showCloseButton?: boolean;
  /**
   * Custom header content (replaces title)
   */
  header?: React.ReactNode;
  /**
   * Maximum height for the modal content
   */
  maxHeight?: string;
  /**
   * Whether the modal content should scroll
   */
  scrollable?: boolean;
  /**
   * Whether clicking outside the modal closes it
   */
  closeOnOverlayClick?: boolean;
  /**
   * Whether pressing Escape closes the modal
   */
  closeOnEscape?: boolean;
}

/**
 * BaseModal component that wraps the common Dialog boilerplate pattern.
 * Use this for consistent modal appearance across the application.
 *
 * @example
 * ```tsx
 * <BaseModal
 *   isOpen={isOpen}
 *   onClose={handleClose}
 *   title="Confirm Action"
 *   footer={
 *     <>
 *       <Button variant="outline" onClick={handleClose}>Cancel</Button>
 *       <Button colorPalette="blue" onClick={handleConfirm}>Confirm</Button>
 *     </>
 *   }
 * >
 *   <Text>Are you sure you want to proceed?</Text>
 * </BaseModal>
 * ```
 */
export function BaseModal({
  isOpen,
  onClose,
  title,
  size = 'md',
  children,
  footer,
  showCloseButton = true,
  header,
  maxHeight = '90vh',
  scrollable = true,
  closeOnOverlayClick = true,
  closeOnEscape = true,
}: BaseModalProps) {
  const handleOpenChange = React.useCallback(
    (e: { open: boolean }) => {
      if (!e.open) {
        onClose();
      }
    },
    [onClose]
  );

  return (
    <Dialog.Root
      open={isOpen}
      onOpenChange={handleOpenChange}
      size={size}
      closeOnInteractOutside={closeOnOverlayClick}
      closeOnEscape={closeOnEscape}
    >
      <Dialog.Backdrop />
      <Dialog.Positioner {...({} as React.ComponentProps<typeof Dialog.Positioner>)}>
        <Dialog.Content>
          <Box
            background="bg.default"
            borderRadius="lg"
            boxShadow="lg"
            maxHeight={scrollable ? maxHeight : undefined}
            overflow={scrollable ? 'auto' : undefined}
          >
            {(title || header || showCloseButton) && (
              <Dialog.Header>
                {header || (
                  <Text fontWeight="semibold" fontSize="lg">
                    {title}
                  </Text>
                )}
                {showCloseButton && <Dialog.CloseTrigger />}
              </Dialog.Header>
            )}

            <Dialog.Body>{children}</Dialog.Body>

            {footer && (
              <Dialog.Footer>
                <HStack gap={2} justifyContent="flex-end" width="100%">
                  {footer}
                </HStack>
              </Dialog.Footer>
            )}
          </Box>
        </Dialog.Content>
      </Dialog.Positioner>
    </Dialog.Root>
  );
}

/**
 * Props for ConfirmModal component
 */
export interface ConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
  title: string;
  message: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  confirmColorPalette?: string;
  isLoading?: boolean;
  size?: 'sm' | 'md' | 'lg';
}

/**
 * Pre-configured confirmation modal for common confirm/cancel dialogs.
 *
 * @example
 * ```tsx
 * <ConfirmModal
 *   isOpen={isOpen}
 *   onClose={handleClose}
 *   onConfirm={handleDelete}
 *   title="Delete Item"
 *   message="Are you sure you want to delete this item? This action cannot be undone."
 *   confirmLabel="Delete"
 *   confirmColorPalette="red"
 *   isLoading={isDeleting}
 * />
 * ```
 */
export function ConfirmModal({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  confirmColorPalette = 'blue',
  isLoading = false,
  size = 'sm',
}: ConfirmModalProps) {
  const handleConfirm = React.useCallback(async () => {
    await onConfirm();
    // Don't close automatically - let the parent handle it after success
  }, [onConfirm]);

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      size={size}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={isLoading}>
            {cancelLabel}
          </Button>
          <Button colorPalette={confirmColorPalette} onClick={handleConfirm} isLoading={isLoading}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      <Text color="#374151">{message}</Text>
    </BaseModal>
  );
}
